// Ficha del enlace en el catálogo (VIDA-372): los campos de la zona derecha de
// la ficha. Qué campos se ven en la web lo decide cada catálogo en «Catalogo
// Web → Estructura de la ficha».

export const SHEET_LINKS = [
  { key: 'book', label: 'Reservar' },
  { key: 'prices', label: 'Ver precios' },
  { key: 'more_info', label: 'Más información' },
  { key: 'product_sheet', label: 'Ficha de producto' },
  { key: 'tech_sheet', label: 'Ficha técnica' },
  { key: 'pdf', label: 'Descargar PDF' },
  { key: 'faq', label: 'Preguntas frecuentes' },
  { key: 'catalog', label: 'Catálogo de producto' },
  { key: 'shop', label: 'Tienda online' },
] as const;
export type SheetLinkKey = (typeof SHEET_LINKS)[number]['key'];

export interface ProductSheet {
  modality: '' | 'individual' | 'grupal';
  date_mode: '' | 'fecha' | 'proximamente' | 'a_demanda';
  start_date: string | null; // AAAA-MM-DD
  duration: string;
  accreditation: string;
  interest: string;
  links: Partial<Record<SheetLinkKey, string>>;
}

export const emptySheet = (raw?: Partial<ProductSheet> | null): ProductSheet => ({
  modality: raw?.modality || '',
  date_mode: raw?.date_mode || '',
  start_date: raw?.start_date ? String(raw.start_date).slice(0, 10) : null,
  duration: raw?.duration || '',
  accreditation: raw?.accreditation || '',
  interest: raw?.interest || '',
  links: { ...(raw?.links || {}) },
});

const inputClass = (editable: boolean) =>
  `w-full rounded-lg border-gray-300 shadow-sm text-sm ${editable ? 'focus:ring-vida-500 focus:border-vida-500' : 'bg-gray-50'}`;
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

export default function ProductSheetFields({ value, onChange, canEdit }: {
  value: ProductSheet;
  onChange: (next: ProductSheet) => void;
  canEdit: boolean;
}) {
  const set = <K extends keyof ProductSheet>(field: K, v: ProductSheet[K]) => onChange({ ...value, [field]: v });
  const setLink = (key: SheetLinkKey, href: string) => onChange({ ...value, links: { ...value.links, [key]: href } });

  return (
    <div className="space-y-5">
      <p className="text-xs text-gray-500">
        Datos de la zona derecha de la ficha. Lo que se ve en la web lo eliges en Catalogo Web → «Estructura de la ficha».
        Los sellos salen de los distintivos del producto (Productos → Categorización).
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className={labelClass}>Modalidad</label>
          <select value={value.modality} disabled={!canEdit} onChange={(e) => set('modality', e.target.value as ProductSheet['modality'])} className={inputClass(canEdit)}>
            <option value="">Sin indicar</option>
            <option value="individual">Individual</option>
            <option value="grupal">Grupal</option>
          </select>
        </div>
        <div>
          <label className={labelClass}>Fecha</label>
          <select value={value.date_mode} disabled={!canEdit} onChange={(e) => set('date_mode', e.target.value as ProductSheet['date_mode'])} className={inputClass(canEdit)}>
            <option value="">Sin indicar</option>
            <option value="fecha">Fecha de inicio</option>
            <option value="proximamente">Próximamente</option>
            <option value="a_demanda">A demanda</option>
          </select>
        </div>
        {value.date_mode === 'fecha' && (
          <div>
            <label className={labelClass}>Inicio</label>
            <input type="date" value={value.start_date ?? ''} readOnly={!canEdit} onChange={(e) => set('start_date', e.target.value || null)} className={inputClass(canEdit)} />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Duración</label>
          <input type="text" value={value.duration} readOnly={!canEdit} onChange={(e) => set('duration', e.target.value)} className={inputClass(canEdit)} placeholder="3 horas, 2 días…" />
        </div>
        <div>
          <label className={labelClass}>Acreditación</label>
          <input type="text" value={value.accreditation} readOnly={!canEdit} onChange={(e) => set('accreditation', e.target.value)} className={inputClass(canEdit)} />
        </div>
      </div>

      <div>
        <label className={labelClass}>Aspectos de interés</label>
        <textarea value={value.interest} readOnly={!canEdit} onChange={(e) => set('interest', e.target.value)} rows={3} className={inputClass(canEdit)} />
      </div>

      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">Enlaces</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {SHEET_LINKS.map((l) => (
            <div key={l.key}>
              <label className="block text-xs text-gray-500 mb-0.5">{l.label}</label>
              <input type="text" value={value.links[l.key] ?? ''} readOnly={!canEdit} onChange={(e) => setLink(l.key, e.target.value.trim())}
                className={inputClass(canEdit)} placeholder="https://… o /ruta" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
